import { useState, useRef, useEffect } from 'react';
import Navbar from '@/components/Navbar';
import PersonalityTest from '@/components/PersonalityTest';
import CareerMatch from '@/components/CareerMatch';
import FutureCareers from '@/components/FutureCareers';
import PricingSection from '@/components/PricingSection';
import Footer from '@/components/Footer';
import { PersonalityType, CareerPreferences, MBTIResult } from '@/utils/personalityTypes';
import { getTopFutureCareers } from '@/utils/careerData';
import { cn } from '@/lib/utils';
import { AuthForm } from '@/components/auth/AuthForm';
import { useAuth } from '@/contexts/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight, LineChart, ShieldCheck, BrainCircuit, Upload, Briefcase, GraduationCap } from 'lucide-react';
import { Button } from '@/components/ui/button';

const Index = () => {
  const [testResult, setTestResult] = useState<{
    personalityType: PersonalityType | null,
    careerPreferences: CareerPreferences | null,
    mbtiResult: MBTIResult | null
  }>({
    personalityType: null,
    careerPreferences: null,
    mbtiResult: null
  });
  const [activeSection, setActiveSection] = useState('home');
  const [showScrollIndicator, setShowScrollIndicator] = useState(true);
  const [authOpen, setAuthOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<string | null>(null);
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  
  const sectionsRef = useRef<Record<string, HTMLElement | null>>({
    home: null,
    'personality-test': null,
    'future-careers': null,
    about: null
  });
  
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 100;
      
      if (scrollPosition > 100) {
        setShowScrollIndicator(false);
      }
      
      Object.entries(sectionsRef.current).forEach(([key, ref]) => {
        if (ref) {
          const offsetTop = ref.offsetTop;
          const height = ref.offsetHeight;
          
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + height) {
            setActiveSection(key);
          }
        }
      });
    };
    
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.substring(1);
      const element = document.getElementById(id);
      
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 300);
      }
    }
  }, [location.hash]);
  
  const handleTestComplete = (result: { 
    personalityType: PersonalityType, 
    careerPreferences: CareerPreferences,
    mbtiResult: MBTIResult
  }) => {
    setTestResult({
      personalityType: result.personalityType,
      careerPreferences: result.careerPreferences,
      mbtiResult: result.mbtiResult
    });
    
    const resultsElement = document.getElementById('personality-results');
    if (resultsElement) {
      setTimeout(() => {
        resultsElement.scrollIntoView({ behavior: 'smooth' });
      }, 500);
    }
  };
  
  const handleActionClick = (target: string) => {
    if (isAuthenticated) {
      const targetElement = document.getElementById(target);
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      setAuthOpen(true);
      setPendingAction(target);
    }
  };
  
  const handleResumeUpload = () => {
    if (isAuthenticated) {
      navigate('/chat', { state: { activeTab: 'resume' } });
    } else {
      setAuthOpen(true);
      setPendingAction('resume-upload');
    }
  };
  
  const handleAuthSuccess = () => {
    if (pendingAction) {
      if (pendingAction === 'resume-upload') {
        navigate('/chat', { state: { activeTab: 'resume' } });
      } else {
        const targetElement = document.getElementById(pendingAction);
        if (targetElement) {
          setTimeout(() => {
            targetElement.scrollIntoView({ behavior: 'smooth' });
          }, 300);
        }
      }
      setPendingAction(null);
    }
  };
  
  useEffect(() => {
    const handleOpenAuth = () => {
      setAuthOpen(true);
      setPendingAction('personality-test');
    };
    
    document.addEventListener('open-auth', handleOpenAuth);
    
    return () => {
      document.removeEventListener('open-auth', handleOpenAuth);
    };
  }, []);
  
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <section 
        id="home" 
        ref={el => sectionsRef.current.home = el} 
        className="relative min-h-screen flex items-center justify-center pt-16 overflow-hidden"
      >
        <div className="absolute inset-0 z-0 bg-gradient-to-b from-primary/5 to-background">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute w-full h-full bg-[url('https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?q=80&w=1470')] bg-cover bg-center animate-pulse-soft"></div>
          </div>
          <div className="absolute inset-0 bg-gradient-radial from-transparent to-background/90"></div>
        </div>
        
        <div className="container mx-auto px-4 z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6 animate-fade-in text-left">
              <div className="inline-block px-3 py-1 bg-primary/10 text-primary text-sm font-medium rounded-full mb-2">
                AI-Powered Career Guidance
              </div>
              <h1 className="text-4xl md:text-6xl font-bold leading-tight">
                Future-Proof Your <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">Career Path</span> With AI
              </h1>
              <p className="text-xl text-muted-foreground max-w-2xl">
                Discover careers that match your personality and will thrive in the age of AI. Get personalized recommendations for both traditional and emerging career paths.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 pt-6">
                <Button
                  onClick={() => handleActionClick('personality-test')}
                  size="lg"
                  className="group relative overflow-hidden"
                >
                  <span className="relative z-10 flex items-center">
                    Take the Career Assessment
                    <ArrowRight className="ml-2 transition-transform group-hover:translate-x-1" />
                  </span>
                  <span className="absolute inset-0 bg-gradient-to-r from-primary to-primary/80 transform transition-transform group-hover:scale-105"></span>
                </Button>
                <Button
                  onClick={() => handleActionClick('future-careers')}
                  variant="outline"
                  size="lg"
                  className="border-2 hover:bg-accent/10 hover:text-accent hover:border-accent/50 transition-all"
                >
                  Explore Career Options
                </Button>
              </div>
              
              <div className="pt-8 flex flex-wrap gap-6">
                <div className="flex items-center text-muted-foreground">
                  <ShieldCheck className="w-5 h-5 mr-2 text-primary" />
                  <span>AI-Resistant Careers</span>
                </div>
                <div className="flex items-center text-muted-foreground">
                  <BrainCircuit className="w-5 h-5 mr-2 text-primary" />
                  <span>Personalized Analysis</span>
                </div>
                <div className="flex items-center text-muted-foreground">
                  <LineChart className="w-5 h-5 mr-2 text-primary" />
                  <span>Future Trends</span>
                </div>
              </div>
            </div>
            
            <div className="hidden lg:block">
              <div className="relative">
                <div className="absolute -top-16 -left-16 w-64 h-64 bg-primary/10 rounded-full filter blur-3xl"></div>
                <div className="absolute -bottom-8 -right-8 w-48 h-48 bg-accent/10 rounded-full filter blur-3xl"></div>
                <div className="relative z-10 glass-card p-6 rounded-2xl rotate-3 animate-float">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                      <GraduationCap className="w-5 h-5 text-primary" />
                    </div>
                    <div className="ml-3">
                      <h3 className="font-bold">Career Match</h3>
                      <p className="text-sm text-muted-foreground">Personalized for you</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span>Data Science</span>
                      <span className="text-primary font-medium">94%</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div className="bg-primary h-2 rounded-full" style={{ width: '94%' }}></div>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span>UX Research</span>
                      <span className="text-accent font-medium">86%</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div className="bg-accent h-2 rounded-full" style={{ width: '86%' }}></div>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span>AI Ethics Specialist</span>
                      <span className="text-secondary font-medium">78%</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div className="bg-secondary h-2 rounded-full" style={{ width: '78%' }}></div>
                    </div>
                  </div>
                </div>
                
                <div className="relative z-20 glass-card p-6 rounded-2xl -rotate-2 mt-8 ml-16 animate-float animation-delay-200">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center">
                      <BrainCircuit className="w-5 h-5 text-accent" />
                    </div>
                    <div className="ml-3">
                      <h3 className="font-bold">Personality Profile</h3>
                      <p className="text-sm text-muted-foreground">Your work style</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="p-2 bg-background/50 rounded text-center">Analytical</div>
                    <div className="p-2 bg-background/50 rounded text-center">Creative</div>
                    <div className="p-2 bg-background/50 rounded text-center">Collaborative</div>
                    <div className="p-2 bg-background/50 rounded text-center">Strategic</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {showScrollIndicator && (
            <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 flex flex-col items-center animate-bounce">
              <span className="text-muted-foreground text-sm mb-2">Scroll Down</span>
              <svg className="w-6 h-6 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
            </div>
          )}
        </div>
      </section>
      
      <section className="py-20 bg-gradient-to-b from-muted/30 to-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <div className="inline-block px-3 py-1 bg-secondary/10 text-secondary text-sm font-medium rounded-full mb-3">
              Our Services
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Navigate Your Career Journey</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Comprehensive tools to guide your career decisions in an AI-transformed world
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="glass-card rounded-xl p-6 transition-all duration-300 hover:shadow-glass-hover animate-slide-up group hover:-translate-y-2">
              <div className="h-12 w-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                <Briefcase className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-xl font-bold mb-2">Career Path Finder</h3>
              <p className="text-muted-foreground mb-4">
                Take our comprehensive assessment to discover careers aligned with your personality and skills.
              </p>
              <button
                onClick={() => handleActionClick('personality-test')}
                className="text-primary flex items-center text-sm font-medium"
              >
                Start Assessment <ArrowRight className="ml-1 w-4 h-4" />
              </button>
            </div>
            
            <div className="glass-card rounded-xl p-6 transition-all duration-300 hover:shadow-glass-hover animate-slide-up animation-delay-100 group hover:-translate-y-2">
              <div className="h-12 w-12 bg-accent/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-accent/20 transition-colors">
                <ShieldCheck className="w-6 h-6 text-accent" />
              </div>
              <h3 className="text-xl font-bold mb-2">AI-Resistant Careers</h3>
              <p className="text-muted-foreground mb-4">
                Explore careers that will remain relevant and valuable despite increasing automation, and discover emerging jobs that will be in high demand in the next 5-10 years.
              </p>
              <button
                onClick={() => handleActionClick('future-careers')}
                className="text-accent flex items-center text-sm font-medium"
              >
                Explore Careers <ArrowRight className="ml-1 w-4 h-4" />
              </button>
            </div>
            
            <div className="glass-card rounded-xl p-6 transition-all duration-300 hover:shadow-glass-hover animate-slide-up animation-delay-200 group hover:-translate-y-2">
              <div className="h-12 w-12 bg-secondary/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-secondary/20 transition-colors">
                <LineChart className="w-6 h-6 text-secondary" />
              </div>
              <h3 className="text-xl font-bold mb-2">Skills Gap Analysis</h3>
              <p className="text-muted-foreground mb-4">
                Identify the skills you need to develop to prepare for your desired future career.
              </p>
              <div className="text-secondary flex items-center text-sm font-medium">
                Coming Soon <ArrowRight className="ml-1 w-4 h-4" />
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="glass-card rounded-2xl overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              <div className="p-8 lg:p-12 flex flex-col justify-center">
                <div className="inline-block px-3 py-1 bg-primary/10 text-primary text-sm font-medium rounded-full mb-3">
                  Quick Analysis
                </div>
                <h2 className="text-3xl font-bold mb-4">Upload Your Resume</h2>
                <p className="text-muted-foreground mb-6">
                  Get instant insights about your career profile and personalized recommendations by uploading your resume.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button
                    onClick={handleResumeUpload}
                    className="flex items-center"
                  >
                    <Upload className="mr-2 h-4 w-4" />
                    Upload Resume
                  </Button>
                  
                  <Button
                    variant="outline"
                    onClick={() => handleActionClick('personality-test')}
                  >
                    Take Assessment Instead
                  </Button>
                </div>
              </div>
              
              <div className="hidden lg:block relative bg-gradient-to-br from-primary/10 to-accent/10 p-12">
                <div className="absolute inset-0">
                  <div className="h-full w-full object-cover opacity-20 bg-[url('https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?q=80&w=1472')] bg-center bg-cover"></div>
                </div>
                
                <div className="relative z-10 flex flex-col h-full justify-center space-y-6">
                  <div className="glass-card p-4 max-w-xs">
                    <div className="flex items-center">
                      <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                        <LineChart className="w-5 h-5 text-primary" />
                      </div>
                      <div className="ml-3">
                        <h3 className="font-bold">Instant Analysis</h3>
                        <p className="text-sm text-muted-foreground">AI-powered insights</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="glass-card p-4 max-w-xs ml-auto">
                    <div className="flex items-center">
                      <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center">
                        <ShieldCheck className="w-5 h-5 text-accent" />
                      </div>
                      <div className="ml-3">
                        <h3 className="font-bold">Skills Assessment</h3>
                        <p className="text-sm text-muted-foreground">Identify your strengths</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="glass-card p-4 max-w-xs">
                    <div className="flex items-center">
                      <div className="w-10 h-10 rounded-full bg-secondary/20 flex items-center justify-center">
                        <GraduationCap className="w-5 h-5 text-secondary" />
                      </div>
                      <div className="ml-3">
                        <h3 className="font-bold">Career Suggestions</h3>
                        <p className="text-sm text-muted-foreground">Personalized for you</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <section 
        id="personality-test" 
        ref={el => sectionsRef.current['personality-test'] = el}
        className="py-20 bg-gradient-to-b from-background to-muted/30"
      >
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <div className="inline-block px-3 py-1 bg-primary/10 text-primary text-sm font-medium rounded-full mb-3">
              Comprehensive Career Assessment
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Find Your Future Path</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Take our personality and career preference assessment to discover your ideal match among both traditional and emerging careers.
            </p>
          </div>
          
          {isAuthenticated ? (
            <PersonalityTest onComplete={handleTestComplete} />
          ) : (
            <div className="text-center py-16">
              <div className="glass-card rounded-xl p-8 max-w-md mx-auto">
                <h3 className="text-2xl font-bold mb-4">Sign In Required</h3>
                <p className="text-muted-foreground mb-6">
                  You need to sign in to take the comprehensive personality and career test.
                </p>
                <button
                  onClick={() => setAuthOpen(true)}
                  className="px-6 py-3 bg-primary text-primary-foreground rounded-lg shadow-sm hover:bg-primary/90 transition-all duration-200 font-medium"
                >
                  Sign In to Continue
                </button>
              </div>
            </div>
          )}
          
          {testResult.personalityType && (
            <div id="personality-results" className="mt-20 pt-10 animate-fade-in">
              <div className="text-center mb-12">
                <div className="inline-block px-3 py-1 bg-accent/10 text-accent text-sm font-medium rounded-full mb-3">
                  Your Results
                </div>
                <h2 className="text-3xl md:text-4xl font-bold mb-4">Your Career Matches</h2>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  Based on your personality profile and preferences, here are careers that align with your strengths in both traditional and emerging fields.
                </p>
              </div>
              
              <CareerMatch result={testResult} />
            </div>
          )}
        </div>
      </section>
      
      <PricingSection />
      
      <section 
        id="future-careers" 
        ref={el => sectionsRef.current['future-careers'] = el}
        className="py-20 bg-muted/30"
      >
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <div className="inline-block px-3 py-1 bg-secondary/10 text-secondary text-sm font-medium rounded-full mb-3">
              AI-Resistant Careers
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Explore Tomorrow's Careers</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Discover careers that will remain valuable and in-demand as technology advances, including both traditional paths and emerging opportunities.
            </p>
          </div>
          
          <FutureCareers />
        </div>
      </section>
      
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <div className="inline-block px-3 py-1 bg-accent/10 text-accent text-sm font-medium rounded-full mb-3">
              Success Stories
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">What Our Users Say</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Hear from students who found their ideal career path using CareerLens
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="glass-card rounded-xl p-6 shadow-glass-hover">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-r from-primary to-accent flex items-center justify-center text-white font-bold">
                  JD
                </div>
                <div className="ml-4">
                  <h4 className="font-bold">Jamie Davis</h4>
                  <p className="text-sm text-muted-foreground">Computer Science Student</p>
                </div>
              </div>
              <p className="text-muted-foreground">
                "CareerLens helped me discover that my skills were perfect for AI ethics - a field I hadn't even considered before. Now I'm focusing my studies in that direction!"
              </p>
            </div>
            
            <div className="glass-card rounded-xl p-6 shadow-glass-hover">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-r from-secondary to-primary flex items-center justify-center text-white font-bold">
                  RL
                </div>
                <div className="ml-4">
                  <h4 className="font-bold">Riley Lee</h4>
                  <p className="text-sm text-muted-foreground">High School Senior</p>
                </div>
              </div>
              <p className="text-muted-foreground">
                "I was unsure about my college major until I used CareerLens. The assessment showed my aptitude for sustainable engineering, which combines my love for science and environmental concerns."
              </p>
            </div>
            
            <div className="glass-card rounded-xl p-6 shadow-glass-hover">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-r from-accent to-secondary flex items-center justify-center text-white font-bold">
                  MT
                </div>
                <div className="ml-4">
                  <h4 className="font-bold">Morgan Taylor</h4>
                  <p className="text-sm text-muted-foreground">Psychology Major</p>
                </div>
              </div>
              <p className="text-muted-foreground">
                "The AI-resistant careers section showed me how my psychology background could lead to a career in human-AI interaction design. It's the perfect blend of my interests!"
              </p>
            </div>
          </div>
        </div>
      </section>
      
      <section 
        id="about" 
        ref={el => sectionsRef.current.about = el}
        className="py-20 bg-gradient-to-b from-muted/30 to-background"
      >
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-block px-3 py-1 bg-accent/10 text-accent text-sm font-medium rounded-full mb-3">
                About Us
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Our Approach to Career Guidance</h2>
              <p className="text-lg text-muted-foreground">
                We combine personality psychology with future trend analysis to help students prepare for careers that may not even exist yet.
              </p>
            </div>
            
            <div className="glass-card rounded-xl p-8 mb-12">
              <h3 className="text-2xl font-bold mb-4">Why We're Different</h3>
              <p className="mb-6">
                Traditional career guidance focuses on matching students to existing jobs. But in today's rapidly changing world, many of the careers today's high school students will hold haven't been invented yet.
              </p>
              <p>
                Our forward-looking approach helps students identify their core strengths and align them with emerging fields and evolving career paths, preparing them for both the present and future job markets.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="glass-card rounded-xl p-6 hover:shadow-glass-hover transition-all duration-300">
                <h3 className="text-xl font-bold mb-3">Data-Driven Predictions</h3>
                <p className="text-muted-foreground">
                  Our career predictions are based on extensive analysis of technological trends, industry developments, and socioeconomic factors that will shape the workforce of tomorrow.
                </p>
              </div>
              
              <div className="glass-card rounded-xl p-6 hover:shadow-glass-hover transition-all duration-300">
                <h3 className="text-xl font-bold mb-3">Personalized Guidance</h3>
                <p className="text-muted-foreground">
                  We don't just show you future careers—we match them to your unique strengths and preferences to help you find meaningful work that aligns with who you are.
                </p>
              </div>
              
              <div className="glass-card rounded-xl p-6 hover:shadow-glass-hover transition-all duration-300">
                <h3 className="text-xl font-bold mb-3">Skill Evolution Focus</h3>
                <p className="text-muted-foreground">
                  We help you understand not just what careers might exist in the future, but what skills you'll need to develop to thrive in them.
                </p>
              </div>
              
              <div className="glass-card rounded-xl p-6 hover:shadow-glass-hover transition-all duration-300">
                <h3 className="text-xl font-bold mb-3">Adaptability Emphasis</h3>
                <p className="text-muted-foreground">
                  In a world of constant change, we prioritize helping students develop adaptability and resilience alongside specific career guidance.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <section className="py-20 bg-primary/5">
        <div className="container mx-auto px-4">
          <div className="glass-card rounded-xl p-8 text-center max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold mb-4">Start Your Future Journey Today</h2>
            <p className="text-lg mb-8 text-muted-foreground">
              Take the first step toward discovering future-ready career paths that align with your unique personality and strengths.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => handleActionClick('personality-test')}
                size="lg"
                className="group relative overflow-hidden"
              >
                <span className="relative z-10 flex items-center">
                  Take the Comprehensive Test
                  <ArrowRight className="ml-2 transition-transform group-hover:translate-x-1" />
                </span>
                <span className="absolute inset-0 bg-gradient-to-r from-primary to-primary/80 transform transition-transform group-hover:scale-105"></span>
              </Button>
              <Button
                onClick={handleResumeUpload}
                variant="outline"
                size="lg"
                className="flex items-center justify-center border-2"
              >
                <Upload className="mr-2 h-4 w-4" />
                Upload Your Resume
              </Button>
            </div>
          </div>
        </div>
      </section>
      
      <Footer />
      
      <AuthForm 
        open={authOpen} 
        onOpenChange={setAuthOpen} 
        onSuccess={handleAuthSuccess}
      />
    </div>
  );
};

export default Index;
